import { API_URL } from '@/config/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, Copy, ExternalLink, ChevronLeft, ChevronRight,
  AlertTriangle, CreditCard, BarChart3, Shield, MessageSquare,
  RefreshCw, Mail, Ban, UserCheck, UserX, Plus, Minus, StickyNote,
  Loader2
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import axios from 'axios';
import AdminNav from '../components/AdminNav';
import AdminTopMenuBar from '../components/AdminTopMenuBar';
import AdminMobileBottomNav from '../components/AdminMobileBottomNav';

const API = API_URL;

const getToken = () => localStorage.getItem('accessToken');
const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

// --- Utility Components ---

const StatusPill = ({ status }) => {
  const map = {
    active: 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/40',
    suspended: 'bg-yellow-500/15 text-yellow-300 ring-yellow-400/40',
    banned: 'bg-red-500/20 text-red-300 ring-red-400/50',
  };
  return (
    <span data-testid="user-status-pill" className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 capitalize ${map[status] || 'bg-slate-700/40 text-slate-200 ring-slate-500/40'}`}>
      {status}
    </span>
  );
};

const RoleBadge = ({ role }) => {
  const colors = {
    reader: 'bg-slate-700/40 text-slate-200 ring-slate-500/40',
    creator: 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/40',
    author: 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/40',
    publisher: 'bg-purple-500/15 text-purple-300 ring-purple-400/40',
    admin: 'bg-yellow-500/15 text-yellow-300 ring-yellow-400/40',
    beta_tester: 'bg-cyan-500/15 text-cyan-300 ring-cyan-400/40',
    promotional: 'bg-pink-500/15 text-pink-300 ring-pink-400/40',
    banned: 'bg-red-500/20 text-red-300 ring-red-400/50',
  };
  const labels = { reader: 'Reader', creator: 'Author', publisher: 'Publisher', admin: 'Admin', beta_tester: 'Beta Tester', promotional: 'Promotional', banned: 'Banned' };
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${colors[role] || colors.reader}`}>{labels[role] || role}</span>;
};

const SubStatusPill = ({ status }) => {
  const map = {
    active: 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/40',
    past_due: 'bg-red-500/20 text-red-300 ring-red-400/50',
    canceled: 'bg-slate-700/40 text-slate-400 ring-slate-500/40',
    trialing: 'bg-blue-500/15 text-blue-300 ring-blue-400/40',
  };
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 capitalize ${map[status] || 'bg-yellow-500/15 text-yellow-300 ring-yellow-400/40'}`}>{status?.replace('_', ' ') || 'unknown'}</span>;
};

const InvStatusPill = ({ status }) => {
  const map = {
    paid: 'text-emerald-400', open: 'text-yellow-400',
    uncollectible: 'text-red-400', void: 'text-slate-500', draft: 'text-slate-500',
  };
  return <span className={`text-xs font-semibold capitalize ${map[status] || 'text-slate-400'}`}>{status}</span>;
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button onClick={handleCopy} className="p-1 rounded hover:bg-slate-700 transition-colors" title="Copy">
      {copied ? <span className="text-xs text-emerald-400">Copied</span> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
    </button>
  );
};

const PanelShell = ({ title, icon: Icon, loading, error, onRetry, children }) => (
  <section className="rounded-2xl bg-slate-900/60 ring-1 ring-slate-800 overflow-hidden">
    <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5 text-slate-400" />}
        <h2 className="text-base font-semibold text-slate-100">{title}</h2>
      </div>
      {onRetry && <button onClick={onRetry} className="p-1.5 rounded-lg hover:bg-slate-800 transition-colors" title="Refresh"><RefreshCw className="w-4 h-4 text-slate-400" /></button>}
    </div>
    <div className="p-5">
      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /><span className="ml-2 text-sm text-slate-400">Loading...</span></div>
      ) : error ? (
        <div className="text-center py-6">
          <p className="text-sm text-red-400 mb-2">{error}</p>
          {onRetry && <Button size="sm" variant="outline" onClick={onRetry} className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">Retry</Button>}
        </div>
      ) : children}
    </div>
  </section>
);

const StatTile = ({ label, value, accent }) => (
  <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-800">
    <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">{label}</p>
    <p className={`text-xl font-bold ${accent || 'text-slate-100'}`}>{value}</p>
  </div>
);

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const extractError = (err) => {
  if (err?.response?.data?.detail) {
    const d = err.response.data.detail;
    return typeof d === 'string' ? d : JSON.stringify(d);
  }
  return err?.message || 'An unexpected error occurred';
};

// --- Panel Components ---

const ProfilePanel = ({ data, loading, error, onRetry }) => (
  <PanelShell title="User Profile" icon={Shield} loading={loading} error={error} onRetry={onRetry}>
    {data && (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-xl font-bold text-slate-50" data-testid="user-full-name">{data.full_name}</h3>
          <StatusPill status={data.account_status} />
          <div className="flex flex-wrap gap-1">{(data.roles || []).map(r => <RoleBadge key={r} role={r} />)}</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-slate-400 block text-xs mb-0.5">Email</span>
            <div className="flex items-center gap-1"><span className="text-slate-200 font-medium" data-testid="user-email">{data.email}</span><CopyButton text={data.email} /></div>
          </div>
          <div>
            <span className="text-slate-400 block text-xs mb-0.5">Joined</span>
            <span className="text-slate-200 font-medium">{fmtDate(data.created_at)}</span>
          </div>
          <div>
            <span className="text-slate-400 block text-xs mb-0.5">Last Login</span>
            <span className="text-slate-200 font-medium">{data.last_login_at ? fmtDateTime(data.last_login_at) : '—'}</span>
          </div>
          <div>
            <span className="text-slate-400 block text-xs mb-0.5">User ID</span>
            <div className="flex items-center gap-1"><code className="text-[11px] text-amber-400/80 font-mono" data-testid="user-id-display">{data.user_id}</code><CopyButton text={data.user_id} /></div>
          </div>
        </div>
      </div>
    )}
  </PanelShell>
);

const CreditsPanel = ({ detail, txData, txLoading, txError, txPage, setTxPage, onRetryTx, onRetryDetail }) => {
  const cs = detail?.credit_summary;
  return (
    <PanelShell title="Credit Balance & Transactions" icon={CreditCard} loading={!detail && !txError} error={(!detail && txError) ? txError : null} onRetry={onRetryDetail}>
      {cs && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile label="Current Balance" value={`${cs.current_balance?.toFixed(2)} RC`} accent="text-emerald-400" />
            <StatTile label="Lifetime Purchased" value={`${cs.lifetime_purchased?.toFixed(2)} RC`} accent="text-blue-400" />
            <StatTile label="Lifetime Consumed" value={`${cs.lifetime_consumed?.toFixed(2)} RC`} accent="text-amber-400" />
            <StatTile label="Lifetime Promotional" value={`${cs.lifetime_promotional?.toFixed(2)} RC`} accent="text-pink-400" />
          </div>
          {txLoading ? (
            <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : txError ? (
            <div className="text-center py-4"><p className="text-sm text-red-400 mb-2">{txError}</p><Button size="sm" variant="outline" onClick={onRetryTx} className="bg-slate-800 border-slate-700 text-slate-200">Retry</Button></div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="transactions-table">
                  <thead className="border-b border-slate-700"><tr className="text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                    <th className="px-3 py-2">Date</th><th className="px-3 py-2">Type</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-right">Balance After</th><th className="px-3 py-2">Description</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {(txData?.items || []).length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-6 text-slate-500 text-sm">No transactions on record.</td></tr>
                    ) : txData.items.map((tx, i) => (
                      <tr key={tx.ledger_id || i} className="hover:bg-slate-800/30">
                        <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{fmtDate(tx.created_at)}</td>
                        <td className="px-3 py-2 text-slate-300 capitalize">{tx.transaction_type ?? tx.type ?? '—'}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${tx.amount > 0 ? 'text-emerald-400' : tx.amount < 0 ? 'text-red-400' : 'text-slate-400'}`}>{tx.amount > 0 ? '+' : ''}{Number(tx.amount).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right text-slate-300">{tx.balance_after != null ? Number(tx.balance_after).toFixed(2) : '—'}</td>
                        <td className="px-3 py-2 text-slate-400 max-w-[200px] truncate">{tx.reason ?? tx.description ?? tx.metadata?.reason ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {txData && txData.total_count > txData.page_size && (
                <div className="flex items-center justify-between pt-2">
                  <span className="text-xs text-slate-400">Page {txData.page} of {Math.ceil(txData.total_count / txData.page_size)}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={txPage <= 1} onClick={() => setTxPage(p => p - 1)} className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"><ChevronLeft className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" disabled={txPage >= Math.ceil(txData.total_count / txData.page_size)} onClick={() => setTxPage(p => p + 1)} className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </PanelShell>
  );
};

const SubscriptionPanel = ({ data, loading, error, onRetry }) => {
  if (loading || error) return <PanelShell title="Stripe Subscription & Billing" icon={CreditCard} loading={loading} error={error} onRetry={onRetry} />;
  if (!data) return null;

  if (!data.has_stripe_record) {
    return (
      <PanelShell title="Stripe Subscription & Billing" icon={CreditCard} onRetry={onRetry}>
        <p className="text-sm text-slate-500">No Stripe record on file for this user.</p>
      </PanelShell>
    );
  }

  if (data.error) {
    return (
      <PanelShell title="Stripe Subscription & Billing" icon={CreditCard} onRetry={onRetry}>
        <p className="text-sm text-red-400">{data.error}</p>
      </PanelShell>
    );
  }

  const hasPastDue = data.subscription_status === 'past_due' || (data.invoices || []).some(inv => inv.status === 'open' || inv.status === 'uncollectible');
  const daysRemaining = data.current_period_end ? Math.max(0, Math.ceil((new Date(data.current_period_end) - new Date()) / 86400000)) : null;

  return (
    <PanelShell title="Stripe Subscription & Billing" icon={CreditCard} onRetry={onRetry}>
      <div className="space-y-5">
        {hasPastDue && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="past-due-banner">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <span className="text-sm font-medium text-red-300">Payment past due — last renewal attempt failed.</span>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-slate-400 block text-xs mb-0.5">Stripe Customer ID</span>
            <div className="flex items-center gap-1">
              <span className="text-slate-200 font-mono text-xs">{data.stripe_customer_id}</span>
              <CopyButton text={data.stripe_customer_id} />
              <a href={`https://dashboard.stripe.com/customers/${data.stripe_customer_id}`} target="_blank" rel="noopener noreferrer" className="p-1 rounded hover:bg-slate-700"><ExternalLink className="w-3.5 h-3.5 text-slate-400" /></a>
            </div>
          </div>
          <div>
            <span className="text-slate-400 block text-xs mb-0.5">Status</span>
            {data.subscription_status ? <SubStatusPill status={data.subscription_status} /> : <span className="text-slate-500 text-sm">No active subscription</span>}
          </div>
          {data.plan_name && <div><span className="text-slate-400 block text-xs mb-0.5">Plan</span><span className="text-slate-200">{data.plan_name} ({data.billing_interval}ly)</span></div>}
          {data.price_per_period != null && <div><span className="text-slate-400 block text-xs mb-0.5">Price</span><span className="text-slate-200">${data.price_per_period}/{data.billing_interval}</span></div>}
          {data.current_period_start && <div><span className="text-slate-400 block text-xs mb-0.5">Period Start</span><span className="text-slate-200">{fmtDate(data.current_period_start)}</span></div>}
          {data.current_period_end && (
            <div>
              <span className="text-slate-400 block text-xs mb-0.5">Next Renewal</span>
              <span className="text-slate-100 font-semibold">{fmtDate(data.current_period_end)}</span>
              {daysRemaining != null && <span className="ml-1 text-xs text-slate-400">({daysRemaining} days)</span>}
            </div>
          )}
          <div><span className="text-slate-400 block text-xs mb-0.5">Cancel at Period End</span><span className="text-slate-200">{data.cancel_at_period_end ? 'Yes' : 'No'}</span></div>
          {data.payment_method_brand && (
            <div><span className="text-slate-400 block text-xs mb-0.5">Payment Method</span><span className="text-slate-200 capitalize">{data.payment_method_brand} ···· {data.payment_method_last4}</span></div>
          )}
        </div>
        {(data.invoices || []).length > 0 && (
          <>
            <h3 className="text-sm font-semibold text-slate-300 pt-2">Invoice History</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead className="border-b border-slate-700"><tr className="text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  <th className="px-3 py-2">Date</th><th className="px-3 py-2">Description</th><th className="px-3 py-2 text-right">Amount Due</th><th className="px-3 py-2 text-right">Paid</th><th className="px-3 py-2">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-800/50">
                  {data.invoices.map(inv => (
                    <tr key={inv.invoice_id} className="hover:bg-slate-800/30">
                      <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{fmtDate(inv.created)}</td>
                      <td className="px-3 py-2 text-slate-400 max-w-[200px] truncate">{inv.description || '—'}</td>
                      <td className="px-3 py-2 text-right text-slate-300">${inv.amount_due?.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-slate-300">${inv.amount_paid?.toFixed(2)}</td>
                      <td className="px-3 py-2"><InvStatusPill status={inv.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </PanelShell>
  );
};

const EngagementPanel = ({ data, loading, error, onRetry }) => (
  <PanelShell title="Audia Engagement" icon={BarChart3} loading={loading} error={error} onRetry={onRetry}>
    {data && (
      data.total_sessions === 0 ? (
        <p className="text-sm text-slate-500">No Audia activity on record.</p>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile label="Total Queries" value={data.total_queries} accent="text-purple-400" />
            <StatTile label="Unique Titles" value={data.unique_titles} accent="text-blue-400" />
            <StatTile label="Total Sessions" value={data.total_sessions} />
            <StatTile label="Avg Queries/Session" value={data.avg_queries_per_session} />
          </div>
          {(data.titles || []).length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="engagement-table">
                <thead className="border-b border-slate-700"><tr className="text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  <th className="px-3 py-2">Title</th><th className="px-3 py-2">Author</th><th className="px-3 py-2">Last Accessed</th><th className="px-3 py-2 text-right">Sessions</th><th className="px-3 py-2 text-right">Queries</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-800/50">
                  {data.titles.map(t => (
                    <tr key={t.manuscript_id} className="hover:bg-slate-800/30">
                      <td className="px-3 py-2 text-slate-200 max-w-[200px] truncate">{t.title}</td>
                      <td className="px-3 py-2 text-slate-400">{t.author_name}</td>
                      <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{fmtDate(t.last_accessed)}</td>
                      <td className="px-3 py-2 text-right text-slate-300">{t.session_count}</td>
                      <td className="px-3 py-2 text-right text-slate-300">{t.total_queries}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )
    )}
  </PanelShell>
);

const AdminActionsPanel = ({ data, loading, error, onRetry, userDetail, onActionComplete }) => {
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustDelta, setAdjustDelta] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustError, setAdjustError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState(''); // suspend | reinstate | ban
  const [dialogReason, setDialogReason] = useState('');
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const [noteText, setNoteText] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState('');

  const userId = userDetail?.user_id;
  const status = userDetail?.account_status;

  const handleAdjust = async () => {
    const delta = parseInt(adjustDelta, 10);
    if (!delta || isNaN(delta)) { setAdjustError('Amount must be a non-zero integer.'); return; }
    if (!adjustReason.trim()) { setAdjustError('Reason is required.'); return; }
    setAdjustLoading(true); setAdjustError('');
    try {
      await axios.post(`${API}/api/admin/users/${userId}/adjust-credits`, { credit_delta: delta, reason: adjustReason.trim() }, authHeader());
      setAdjustOpen(false); setAdjustDelta(''); setAdjustReason('');
      onActionComplete(['detail', 'transactions', 'admin-log']);
    } catch (e) { setAdjustError(extractError(e)); }
    finally { setAdjustLoading(false); }
  };

  const openStatusDialog = (action) => {
    setDialogAction(action); setDialogReason(''); setDialogError(''); setDialogOpen(true);
  };

  const handleStatusChange = async () => {
    if (!dialogReason.trim()) { setDialogError('Reason is required.'); return; }
    const statusMap = { suspend: 'suspended', reinstate: 'active', ban: 'banned' };
    setDialogLoading(true); setDialogError('');
    try {
      await axios.post(`${API}/api/admin/users/${userId}/set-status`, { status: statusMap[dialogAction], reason: dialogReason.trim() }, authHeader());
      setDialogOpen(false);
      const refreshPanels = dialogAction === 'ban' ? ['detail', 'transactions', 'admin-log'] : ['detail', 'admin-log'];
      onActionComplete(refreshPanels);
    } catch (e) { setDialogError(extractError(e)); }
    finally { setDialogLoading(false); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) { setNoteError('Note cannot be empty.'); return; }
    setNoteLoading(true); setNoteError('');
    try {
      await axios.post(`${API}/api/admin/users/${userId}/notes`, { note: noteText.trim() }, authHeader());
      setNoteText('');
      onActionComplete(['admin-log']);
    } catch (e) { setNoteError(extractError(e)); }
    finally { setNoteLoading(false); }
  };

  const dialogLabels = {
    suspend: { title: 'Suspend Account', desc: 'This will suspend the user account. The user will not be able to log in.', confirm: 'Suspend', color: 'bg-yellow-600 hover:bg-yellow-700' },
    reinstate: { title: 'Reinstate Account', desc: 'This will reinstate the user account, restoring full access.', confirm: 'Reinstate', color: 'bg-emerald-600 hover:bg-emerald-700' },
    ban: { title: 'Ban Account', desc: 'This will permanently ban this user and forfeit all remaining credits. This action cannot be undone.', confirm: 'Ban User', color: 'bg-red-600 hover:bg-red-700' },
  };
  const dl = dialogLabels[dialogAction] || {};

  return (
    <PanelShell title="Admin Actions & Notes" icon={MessageSquare} loading={loading} error={error} onRetry={onRetry}>
      <div className="space-y-5">
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => { setAdjustOpen(!adjustOpen); setAdjustError(''); }} className="bg-indigo-600/80 hover:bg-indigo-600 text-white" data-testid="adjust-credits-btn">
            {adjustOpen ? <Minus className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}Adjust Credits
          </Button>
          {status === 'active' && (
            <Button size="sm" onClick={() => openStatusDialog('suspend')} className="bg-yellow-600/80 hover:bg-yellow-600 text-white" data-testid="suspend-btn">
              <UserX className="w-4 h-4 mr-1" />Suspend
            </Button>
          )}
          {status === 'suspended' && (
            <Button size="sm" onClick={() => openStatusDialog('reinstate')} className="bg-emerald-600/80 hover:bg-emerald-600 text-white" data-testid="reinstate-btn">
              <UserCheck className="w-4 h-4 mr-1" />Reinstate
            </Button>
          )}
          {status !== 'banned' && (
            <Button size="sm" onClick={() => openStatusDialog('ban')} className="bg-red-600/80 hover:bg-red-600 text-white" data-testid="ban-btn">
              <Ban className="w-4 h-4 mr-1" />Ban
            </Button>
          )}
          {userDetail?.email && (
            <a href={`mailto:${userDetail.email}`} className="inline-flex items-center gap-1 rounded-md bg-slate-700/60 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-700 transition-colors" data-testid="send-email-btn">
              <Mail className="w-4 h-4 mr-1" />Send Email
            </a>
          )}
        </div>

        {/* Adjust Credits Inline Form */}
        {adjustOpen && (
          <div className="p-4 rounded-lg bg-slate-950/50 border border-indigo-500/30 space-y-3" data-testid="adjust-credits-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Amount (positive or negative)</label>
                <Input type="number" value={adjustDelta} onChange={e => setAdjustDelta(e.target.value)} placeholder="e.g. 25 or -10" className="bg-slate-900 border-slate-700 text-slate-100" data-testid="adjust-amount-input" />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Reason (required)</label>
                <Input value={adjustReason} onChange={e => setAdjustReason(e.target.value)} placeholder="Why are you adjusting credits?" className="bg-slate-900 border-slate-700 text-slate-100" data-testid="adjust-reason-input" />
              </div>
            </div>
            {adjustError && <p className="text-xs text-red-400">{adjustError}</p>}
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdjust} disabled={adjustLoading} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="adjust-confirm-btn">{adjustLoading ? 'Saving...' : 'Confirm'}</Button>
              <Button size="sm" variant="outline" onClick={() => setAdjustOpen(false)} className="bg-slate-800 border-slate-700 text-slate-200">Cancel</Button>
            </div>
          </div>
        )}

        {/* Status Dialog */}
        <Dialog open={dialogOpen} onOpenChange={v => { if (!dialogLoading) setDialogOpen(v); }}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">{dl.title}</DialogTitle>
              <DialogDescription className="text-slate-400">{dl.desc}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Reason (required)</label>
                <Input value={dialogReason} onChange={e => setDialogReason(e.target.value)} placeholder="Provide a reason..." className="bg-slate-950/50 border-slate-700 text-slate-100" data-testid="status-reason-input" />
              </div>
              {dialogError && <p className="text-xs text-red-400" data-testid="status-error">{dialogError}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={() => setDialogOpen(false)} disabled={dialogLoading} className="bg-slate-800 border-slate-700 text-slate-200">Cancel</Button>
                <Button size="sm" onClick={handleStatusChange} disabled={dialogLoading} className={`text-white ${dl.color}`} data-testid="status-confirm-btn">{dialogLoading ? 'Processing...' : dl.confirm}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Action Log Table */}
        <div>
          <h3 className="text-sm font-semibold text-slate-300 mb-2">Action Log</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-log-table">
              <thead className="border-b border-slate-700"><tr className="text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                <th className="px-3 py-2">Date</th><th className="px-3 py-2">Admin</th><th className="px-3 py-2">Action</th><th className="px-3 py-2">Details</th><th className="px-3 py-2 text-right">Credits</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-800/50">
                {(!data?.items || data.items.length === 0) ? (
                  <tr><td colSpan={5} className="text-center py-6 text-slate-500 text-sm">No admin actions on record.</td></tr>
                ) : data.items.map(a => (
                  <tr key={a.action_id} className="hover:bg-slate-800/30">
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{fmtDateTime(a.created_at)}</td>
                    <td className="px-3 py-2 text-slate-400 text-xs font-mono">{a.admin_user_id?.substring(0, 8)}...</td>
                    <td className="px-3 py-2 text-slate-200 capitalize">{a.action_type?.replace('_', ' ')}</td>
                    <td className="px-3 py-2 text-slate-400 max-w-[250px] truncate">{a.detail || '—'}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${a.credit_delta > 0 ? 'text-emerald-400' : a.credit_delta < 0 ? 'text-red-400' : 'text-slate-500'}`}>
                      {a.credit_delta != null ? (a.credit_delta > 0 ? '+' : '') + Number(a.credit_delta).toFixed(2) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes Section */}
        <div className="pt-2">
          <h3 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-1"><StickyNote className="w-4 h-4" />Add Note</h3>
          <textarea
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            rows={3}
            placeholder="Write an admin note..."
            className="w-full rounded-lg bg-slate-950/50 border border-slate-700 text-slate-100 px-3 py-2 text-sm placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
            data-testid="note-textarea"
          />
          {noteError && <p className="text-xs text-red-400 mt-1">{noteError}</p>}
          <div className="flex justify-end mt-2">
            <Button size="sm" onClick={handleAddNote} disabled={noteLoading || !noteText.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-note-btn">{noteLoading ? 'Saving...' : 'Add Note'}</Button>
          </div>
        </div>
      </div>
    </PanelShell>
  );
};

// --- Main Page ---

export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState('');

  const [txData, setTxData] = useState(null);
  const [txLoading, setTxLoading] = useState(true);
  const [txError, setTxError] = useState('');
  const [txPage, setTxPage] = useState(1);

  const [subData, setSubData] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [subError, setSubError] = useState('');

  const [engData, setEngData] = useState(null);
  const [engLoading, setEngLoading] = useState(true);
  const [engError, setEngError] = useState('');

  const [logData, setLogData] = useState(null);
  const [logLoading, setLogLoading] = useState(true);
  const [logError, setLogError] = useState('');

  const fetchDetail = useCallback(async () => {
    setDetailLoading(true); setDetailError('');
    try {
      const { data } = await axios.get(`${API}/api/admin/users/${userId}/detail`, authHeader());
      setDetail(data);
    } catch (e) { setDetailError(extractError(e)); }
    finally { setDetailLoading(false); }
  }, [userId]);

  const fetchTransactions = useCallback(async (page = 1) => {
    setTxLoading(true); setTxError('');
    try {
      const { data } = await axios.get(`${API}/api/admin/users/${userId}/transactions`, { ...authHeader(), params: { page, page_size: 20 } });
      setTxData(data);
    } catch (e) { setTxError(extractError(e)); }
    finally { setTxLoading(false); }
  }, [userId]);

  const fetchSubscription = useCallback(async () => {
    setSubLoading(true); setSubError('');
    try {
      const { data } = await axios.get(`${API}/api/admin/users/${userId}/subscription`, authHeader());
      setSubData(data);
    } catch (e) { setSubError(extractError(e)); }
    finally { setSubLoading(false); }
  }, [userId]);

  const fetchEngagement = useCallback(async () => {
    setEngLoading(true); setEngError('');
    try {
      const { data } = await axios.get(`${API}/api/admin/users/${userId}/engagement`, authHeader());
      setEngData(data);
    } catch (e) { setEngError(extractError(e)); }
    finally { setEngLoading(false); }
  }, [userId]);

  const fetchAdminLog = useCallback(async () => {
    setLogLoading(true); setLogError('');
    try {
      const { data } = await axios.get(`${API}/api/admin/users/${userId}/admin-log`, authHeader());
      setLogData(data);
    } catch (e) { setLogError(extractError(e)); }
    finally { setLogLoading(false); }
  }, [userId]);

  useEffect(() => { fetchDetail(); fetchTransactions(1); fetchSubscription(); fetchEngagement(); fetchAdminLog(); }, [fetchDetail, fetchTransactions, fetchSubscription, fetchEngagement, fetchAdminLog]);
  useEffect(() => { fetchTransactions(txPage); }, [txPage, fetchTransactions]);

  const handleActionComplete = (panels) => {
    if (panels.includes('detail')) fetchDetail();
    if (panels.includes('transactions')) { setTxPage(1); fetchTransactions(1); }
    if (panels.includes('admin-log')) fetchAdminLog();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <AdminNav />
      <AdminTopMenuBar />
      <div className="pb-20 md:pb-0">
        <header className="bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-b border-slate-800">
          <div className="mx-auto max-w-7xl px-4 py-6">
            <button onClick={() => navigate('/admin/users')} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 transition-colors mb-3" data-testid="back-to-users-btn">
              <ArrowLeft className="w-4 h-4" />Back to Users
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold" data-testid="user-detail-heading">
              {detailLoading ? 'Loading...' : detail?.full_name || 'User Detail'}
            </h1>
          </div>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-6 space-y-6">
          <ProfilePanel data={detail} loading={detailLoading} error={detailError} onRetry={fetchDetail} />
          <CreditsPanel detail={detail} txData={txData} txLoading={txLoading} txError={txError} txPage={txPage} setTxPage={setTxPage} onRetryTx={() => fetchTransactions(txPage)} onRetryDetail={fetchDetail} />
          <SubscriptionPanel data={subData} loading={subLoading} error={subError} onRetry={fetchSubscription} />
          <EngagementPanel data={engData} loading={engLoading} error={engError} onRetry={fetchEngagement} />
          <AdminActionsPanel data={logData} loading={logLoading} error={logError} onRetry={fetchAdminLog} userDetail={detail} onActionComplete={handleActionComplete} />
        </main>
      </div>
      <AdminMobileBottomNav />
    </div>
  );
}
